import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarPlus, Calendar, MapPin, Users } from 'lucide-react';
import { type Event, EventStatus, EVENT_STATUS_LABELS } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import { useEvents, useEventTypes } from '../hooks/useEvents';
import { EntityList, StatusBadge, FilterSelect } from '../components/common';
import type { EntityListColumn, SortDirection } from '../components/common';
import { EntityModal } from '../components/common/EntityModal';
import { CreateEventPanel } from '../components/events';
import { RecordConversations } from '../components/messaging/RecordConversations';
import { useThreadCounts } from '../hooks/useThreadCounts';

const STATUS_COLORS: Record<string, string> = {
  PLANNING: 'blue',
  CONFIRMED: 'green',
  IN_PROGRESS: 'yellow',
  COMPLETED: 'gray',
  CANCELLED: 'red',
};

const PAGE_SIZE = 25;

// Status options for the multi-select filter, in enum declaration order.
const STATUS_OPTIONS = (Object.keys(EVENT_STATUS_LABELS) as EventStatus[]).map((value) => ({
  id: value,
  name: EVENT_STATUS_LABELS[value],
}));

// Default selection: every status except the two "closed" ones.
const DEFAULT_STATUS_FILTER = STATUS_OPTIONS.map((s) => s.id).filter(
  (id) => id !== EventStatus.COMPLETED && id !== EventStatus.CANCELLED,
);

// Accessors used for client-side column sorting (keyed by column sortKey).
const SORT_ACCESSORS: Record<string, (event: Event) => string | number | Date | null | undefined> =
  {
    name: (e) => e.name,
    date: (e) => (e.startAt ? new Date(e.startAt) : null),
    venue: (e) => (e as { venueName?: string }).venueName,
    guests: (e) => (e as { guestCount?: number }).guestCount ?? 0,
    status: (e) => EVENT_STATUS_LABELS[e.status as keyof typeof EVENT_STATUS_LABELS] || e.status,
  };

function isNil(v: unknown): boolean {
  return v === null || v === undefined;
}

function compareValues(a: string | number | Date, b: string | number | Date): number {
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function EventsPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>(DEFAULT_STATUS_FILTER);
  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string | null>('date');
  const [sortDir, setSortDir] = useState<SortDirection | null>('asc');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const { data: allEvents = [], isLoading } = useEvents();
  const { data: eventTypes = [] } = useEventTypes();

  // Client-side filtering (status + type + search), then sort, then paginate.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allEvents.filter((e) => {
      if (statusFilter.length > 0 && !statusFilter.includes(e.status)) return false;
      if (eventTypeFilter.length > 0 && !eventTypeFilter.includes(e.eventTypeId)) return false;
      if (term) {
        const matches =
          e.name.toLowerCase().includes(term) ||
          (e as any).venueName?.toLowerCase().includes(term) ||
          (e as any).eventTypeName?.toLowerCase().includes(term);
        if (!matches) return false;
      }
      return true;
    });
  }, [allEvents, statusFilter, eventTypeFilter, search]);

  const sorted = useMemo(() => {
    if (!sortBy || !sortDir) return filtered;
    const accessor = SORT_ACCESSORS[sortBy];
    if (!accessor) return filtered;
    const sign = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      const aNil = isNil(va);
      const bNil = isNil(vb);
      // Nil values always sort to the end, regardless of direction.
      if (aNil && bNil) return 0;
      if (aNil) return 1;
      if (bNil) return -1;
      return sign * compareValues(va as string | number | Date, vb as string | number | Date);
    });
  }, [filtered, sortBy, sortDir]);

  const total = sorted.length;
  const items = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleSortChange = useCallback((nextBy: string | null, nextDir: SortDirection | null) => {
    setSortBy(nextBy);
    setSortDir(nextDir);
    setPage(1);
  }, []);

  /*
   * One request for every row's badge, keyed on **the visible page** — this
   * said "the page's own ids" while passing every sorted event, so a producer
   * with six hundred events asked about all six hundred to render twenty-five
   * badges.
   */
  const { countFor } = useThreadCounts(
    'Event',
    items.map((event) => event.id),
  );

  const columns: EntityListColumn<Event>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Event',
        sortable: true,
        render: (event) => (
          <div>
            <p className="text-gray-900 font-semibold flex items-center gap-1.5">
              <span>{event.name}</span>
              <RecordConversations
                entityType="Event"
                entityId={event.id}
                variant="icon"
                count={countFor(event.id)}
              />
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              {(event as any).eventTypeName && (
                <span className="text-xs text-primary-600">{(event as any).eventTypeName}</span>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'date',
        header: 'Date',
        sortable: true,
        render: (event) => (
          <span className="text-sm text-gray-600 flex items-center gap-1">
            <Calendar size={12} className="text-gray-400" />
            {event.startAt
              ? new Date(event.startAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : '—'}
          </span>
        ),
        width: 'w-32',
      },
      {
        key: 'venue',
        header: 'Venue',
        sortable: true,
        render: (event) =>
          (event as any).venueName ? (
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <MapPin size={12} className="text-gray-400" />
              {(event as any).venueName}
            </span>
          ) : (
            <span className="text-gray-400 text-sm">—</span>
          ),
        hideOnMobile: true,
      },
      {
        key: 'guests',
        header: 'Guests',
        sortable: true,
        render: (event) => (
          <span className="text-sm text-gray-600 flex items-center gap-1">
            <Users size={12} className="text-gray-400" />
            {(event as any).guestCount ?? 0}
          </span>
        ),
        width: 'w-20',
        hideOnMobile: true,
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: (event) => (
          <StatusBadge
            label={
              EVENT_STATUS_LABELS[event.status as keyof typeof EVENT_STATUS_LABELS] || event.status
            }
            color={STATUS_COLORS[event.status] || 'gray'}
          />
        ),
        width: 'w-28',
      },
    ],
    // `countFor` changes identity exactly when the counts arrive. Omitted, this
    // memo keeps the closure from the first render — which answers `null`
    // forever, so no badge ever appears.
    [countFor],
  );

  if (!currentUser) return null;

  return (
    <div>
      <EntityList
        items={items}
        columns={columns}
        getKey={(event) => event.id}
        isLoading={isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={handleSortChange}
        searchValue={search}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search events..."
        onRowClick={(event) => navigate(`/events/${event.id}`)}
        onPreview={(event) => navigate(`/events/${event.id}`)}
        title="Events"
        titleActions={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <CalendarPlus size={16} />
            New Event
          </button>
        }
        filterBar={
          <div className="flex items-center gap-2 flex-wrap">
            <FilterSelect
              label="Status"
              multiple
              searchable={false}
              items={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value as string[]);
                setPage(1);
              }}
              getValue={(s) => s.id}
              getLabel={(s) => s.name}
            />
            <FilterSelect
              label="Type"
              multiple
              items={eventTypes}
              value={eventTypeFilter}
              onChange={(value) => {
                setEventTypeFilter(value as string[]);
                setPage(1);
              }}
              getValue={(t) => t.id}
              getLabel={(t) => t.name}
            />
          </div>
        }
        emptyMessage="No events found"
      />

      {/* Create Event Modal */}
      <EntityModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Event"
        size="md"
      >
        <CreateEventPanel
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      </EntityModal>
    </div>
  );
}
